import type { TupleToUnion } from 'type-fest'

import type { IconFill } from '../types/core.ts'
import {
  type RemixFillIconKey,
  RemixFillIcons,
  RemixIconKeys,
  RemixLineIconKeys,
  type RemixOtherIconKey,
  RemixOtherIconKeys,
} from './remix.ts'

// Key to Name

type StripRemixPrefix<T extends string> = T extends `Ri${infer R}` ? R : never

type StripRemixSuffix<T extends string> = T extends `${infer R}Fill` ? R : never

export const RemixFillIconNames = RemixIconKeys.map((icon: string) =>
  icon.replace(/^Ri/, '').replace(/Fill$/, '')
) as ReadonlyArray<StripRemixPrefix<StripRemixSuffix<RemixFillIconKey>>>

export const RemixOtherIconNames = RemixOtherIconKeys.map((icon: string) =>
  icon.replace(/^Ri/, '')
) as ReadonlyArray<StripRemixPrefix<RemixOtherIconKey>>

export const RemixIconNames = [...RemixFillIconNames, ...RemixOtherIconNames]

export type RemixFillIconName = TupleToUnion<typeof RemixFillIconNames>
export type RemixOtherIconName = TupleToUnion<typeof RemixOtherIconNames>
export type RemixIconName = RemixFillIconName | RemixOtherIconName

export const isRemixIconName = (name: unknown): name is RemixIconName => {
  return RemixIconNames.includes(name as RemixIconName)
}

// Name to Key

type RemixFillKey<N extends string> = `Ri${N}Fill`
type RemixLineKey<N extends string> = `Ri${N}Line`
type RemixKey<N extends string, V extends IconFill> = V extends 'solid'
  ? RemixFillKey<N>
  : RemixLineKey<N>

export function remixIconNameToKey<N extends RemixIconName>(
  name: N,
  variant: 'solid'
): keyof typeof RemixFillIcons
export function remixIconNameToKey<N extends RemixIconName>(
  name: N,
  variant: 'outline'
): keyof typeof RemixLineIconKeys
export function remixIconNameToKey<N extends RemixIconName, V extends IconFill>(
  name: N,
  variant: V
): RemixKey<N, V> {
  return `Ri${name}${RemixOtherIconNames.includes(name as RemixOtherIconName) ? '' : variant === 'solid' ? 'Fill' : 'Line'}` as RemixKey<
    N,
    V
  >
}

type SvgLike = React.ComponentType<React.SVGProps<SVGSVGElement>>
type AsSvgLike<T> = { [K in keyof T]: SvgLike }

export const RemixFillIconComponents = RemixFillIcons as unknown as AsSvgLike<
  typeof RemixFillIcons
>

export const RemixLineIconComponents =
  RemixLineIconKeys as unknown as AsSvgLike<typeof RemixLineIconKeys>
